import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import dayjs from "dayjs";
import CgIcon from "../common/CgIcon";
import { formatRupees } from "../common/money";
import styles from "./saleEntry.module.css";

/* The same tones the Billing list gives a bill, so a status reads the same
   wherever it appears. */
const STATUS_TONE = {
  pending: ["#FFFAEB", "#B54708"],
  partial_payment: ["#FFFAEB", "#B54708"],
  paid: ["#ECFDF3", "#027A48"],
  cancelled: ["#F2F4F7", "#475467"],
};

const prettyStatus = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

/** A bill can only be added to while it is still open. */
export const isBillOpen = (bill) =>
  bill?.status !== "paid" && bill?.status !== "cancelled";

const billDate = (bill) => {
  const raw = bill?.created_at || bill?.bill_date || bill?.date;
  const parsed = raw ? dayjs(raw) : null;
  return parsed?.isValid() ? parsed.format("DD MMM YYYY") : "";
};

/**
 * Offered when the customer already has bills: this order can go onto one of
 * them instead of starting another.
 *
 * Shown once the customer is chosen, so the decision is made before any
 * medicine is added - the bill an order sits on is fixed when the order is
 * created and cannot be moved afterwards. The ones still open lead; the settled
 * ones stay on screen greyed out, because "this bill is closed" is a better
 * answer than a bill that is simply missing.
 */
const BillPickerModal = ({
  open,
  bills,
  selectedBillId,
  onSelect,
  onCreateNew,
  onCancel,
  customerName,
  customerMobile,
  /* What this sale comes to, so a row can say what it would become. */
  addingAmount = 0,
}) => {
  const [picked, setPicked] = useState(selectedBillId ?? null);

  useEffect(() => {
    if (open) setPicked(selectedBillId ?? null);
  }, [open, selectedBillId]);

  /* Open first, then the rest - the order they are useful in. */
  const ordered = useMemo(
    () =>
      [...(bills || [])].sort(
        (a, b) => (isBillOpen(b) ? 1 : 0) - (isBillOpen(a) ? 1 : 0)
      ),
    [bills]
  );

  const openCount = ordered.filter(isBillOpen).length;
  const subtitle = [
    customerName || "Counter sale",
    customerMobile,
    ordered.length
      ? `${ordered.length} ${ordered.length === 1 ? "bill" : "bills"} · ${openCount} open`
      : "no bills yet",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal
      className={styles.seDialog}
      open={open}
      centered
      width={540}
      onCancel={onCancel}
      closeIcon={<span className={styles.iactGlyph}>✕</span>}
      title={
        <div style={{ minWidth: 0 }}>
          <div className={styles.seModalT}>Select Bill</div>
          <div className={styles.seModalS}>{subtitle}</div>
        </div>
      }
      footer={
        <div className={styles.seModalF}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnG}`}
            onClick={onCreateNew}
          >
            <CgIcon name="plus" size={15} stroke={2.2} />
            Create New Bill
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className={`${styles.btn} ${styles.btnG}`}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnP}`}
            disabled={!picked}
            onClick={() => picked && onSelect(picked)}
          >
            OK
          </button>
        </div>
      }
    >
      {!ordered.length ? (
        <div className={styles.seEmptyBill}>
          <CgIcon name="billing" size={26} stroke={1.7} />
          <b>No existing bills</b>
          <i>{customerName || "This customer"} has not been billed here before.</i>
        </div>
      ) : (
        <div className={styles.batchList}>
          {ordered.map((bill, index) => {
            const openBill = isBillOpen(bill);
            const on = picked === bill.id;
            const total = Number(bill.grand_total ?? bill.total_amount ?? 0);
            const tone = STATUS_TONE[bill.status] || STATUS_TONE.cancelled;

            return (
              <label
                key={bill.id}
                className={[
                  styles.seBatch,
                  styles.acct,
                  openBill ? null : styles.off,
                  on ? styles.on : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ "--i": index }}
              >
                {openBill && (
                  <input
                    type="radio"
                    name="pharmacy-bill-pick"
                    checked={on}
                    onChange={() => setPicked(bill.id)}
                  />
                )}
                <span className={styles.seBatchDot} />
                <span className={styles.seBatchM}>
                  <span className={styles.seBatchTop}>
                    <b>{bill.bill_number || "Bill"}</b>
                    <span
                      className={styles.bdg}
                      style={{ background: tone[0], color: tone[1] }}
                    >
                      <i />
                      {prettyStatus(bill.status)}
                    </span>
                  </span>
                  <i>
                    {[billDate(bill), openBill ? null : "closed, cannot be added to"]
                      .filter(Boolean)
                      .join(" · ")}
                  </i>
                </span>
                <span className={styles.seBatchP}>
                  <b className={styles.tnum}>{formatRupees(total)}</b>
                  {openBill && addingAmount > 0 ? (
                    <i className={`${styles.tnum} ${styles.seUp}`}>
                      + {formatRupees(addingAmount)} after this sale
                    </i>
                  ) : (
                    <i>bill amount</i>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default BillPickerModal;
